import { queryOptions } from '@tanstack/react-query'
import type { Database } from '@/types/supabase'
import { supabase } from '@/lib/supabase/client'

export type OrganizationInvitation =
  Database['public']['Tables']['organization_invitations']['Row']

/**
 * Organization invitation with profile details
 * profiles can be null (user may not exist yet for pending invitations)
 */
export interface OrganizationInvitationWithProfile extends OrganizationInvitation {
  profiles: {
    email: string
    full_name: string | null
    avatar_url: string | null
  } | null
}

export const organizationInvitation = {
  /**
   * Find all pending (non-expired) invitations for a specific organization.
   *
   * Used for frontend validation to prevent duplicate invitations.
   *
   * Access control (enforced by RLS):
   * - Super admins: Can access ANY organization's pending invitations
   * - Org admins: Can only access invitations for organizations they are admin of
   * - Regular org members: Cannot access invitations (only see their own)
   * - Non-members: Cannot access any invitations
   *
   * This is enforced server-side via RLS policies.
   */
  findManyPendingByOrg: (orgId: string) =>
    queryOptions({
      queryKey: ['organization-invitations', 'org', orgId, 'pending'],
      queryFn: async (): Promise<OrganizationInvitationWithProfile[]> => {
        if (!orgId || orgId.trim() === '') {
          throw new Error(
            'Invalid orgId: cannot fetch invitations without valid organization ID'
          )
        }

        const { data, error } = await supabase
          .from('organization_invitations')
          .select('*, profiles!user_id(email, full_name, avatar_url)')
          .eq('org_id', orgId)
          .is('accepted_at', null)
          .gt('expires_at', new Date().toISOString())
          .order('profiles(full_name)', { ascending: true, nullsFirst: false })
          .order('profiles(email)', { ascending: true })

        if (error) {
          throw new Error(
            `Failed to fetch organization invitations: ${error.message}`,
            {
              cause: error,
            }
          )
        }

        return (data || []) as unknown as OrganizationInvitationWithProfile[]
      },
      // Cache for 5 minutes (admin dashboards don't need real-time data)
      staleTime: 5 * 60 * 1000,
    }),

  /** Pending (non-expired) invitations addressed to this user. */
  findManyPendingByUser: (userId: string) =>
    queryOptions({
      queryKey: ['organization-invitations', 'user', userId, 'pending'],
      queryFn: async (): Promise<OrganizationInvitation[]> => {
        if (!userId || userId.trim() === '') {
          throw new Error(
            'Invalid userId: cannot fetch invitations without valid user ID'
          )
        }

        const { data, error } = await supabase
          .from('organization_invitations')
          .select('*')
          .eq('user_id', userId)
          .is('accepted_at', null)
          .gt('expires_at', new Date().toISOString())
          .order('created_at', { ascending: false })

        if (error) {
          throw new Error(
            `Failed to fetch your invitations: ${error.message}`,
            {
              cause: error,
            }
          )
        }

        return data
      },
    }),
}
